import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Dumbbell } from "lucide-react";
import { useCelebration } from "../../contexts/CelebrationContext";

const ORBIT_COUNT = 6;

// Full-screen milestone celebration — Scarlet theme (accent red glow on a dark scrim, same
// palette as the rest of the app), gym-specific iconography only (orbiting dumbbells + one
// action-specific lucide icon, e.g. Wallet for a payment, UserPlus for a new member). Mounted
// once at the App root (see App.js), above every route, so it survives a navigation that
// happens mid-animation (e.g. password-change routing straight to the dashboard).
export function CelebrationOverlay() {
  const { active } = useCelebration();
  const reducedMotion = useReducedMotion();
  const Icon = active?.icon;

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          key={active.id}
          data-testid="celebration-overlay"
          className="fixed inset-0 z-[999] flex items-center justify-center overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          style={{
            background: "radial-gradient(circle at 50% 45%, rgba(214, 32, 43,0.28), rgba(8,11,18,0.94) 72%)",
            backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
          }}
        >
          {!reducedMotion && [...Array(ORBIT_COUNT)].map((_, i) => {
            const angle = (i * 360) / ORBIT_COUNT;
            const rad = (angle * Math.PI) / 180;
            return (
              <motion.div
                key={i}
                className="absolute"
                style={{ color: "rgba(255,255,255,0.16)" }}
                initial={{ opacity: 0, x: 0, y: 0, rotate: angle }}
                animate={{ opacity: [0, 1, 1, 0], x: Math.cos(rad) * 170, y: Math.sin(rad) * 170, rotate: angle + 50 }}
                transition={{ duration: 1.7, ease: [0.22, 1, 0.36, 1] }}
              >
                <Dumbbell size={26} strokeWidth={1.6} />
              </motion.div>
            );
          })}

          <motion.div
            className="relative flex flex-col items-center gap-4 px-6"
            initial={reducedMotion ? { opacity: 0 } : { scale: 0.6, opacity: 0 }}
            animate={reducedMotion ? { opacity: 1 } : { scale: [0.6, 1.08, 1], opacity: 1 }}
            exit={reducedMotion ? { opacity: 0 } : { scale: 0.85, opacity: 0 }}
            transition={{ duration: 0.55, ease: [0.34, 1.56, 0.64, 1] }}
          >
            <motion.div
              className="rounded-full flex items-center justify-center"
              style={{ width: 108, height: 108, background: "linear-gradient(145deg, #D6202B, #B01B24)" }}
              animate={reducedMotion ? undefined : { boxShadow: ["0 0 0 0 rgba(214, 32, 43,0.4)", "0 0 0 20px rgba(214, 32, 43,0)"] }}
              transition={{ duration: 1.3, ease: "easeOut" }}
            >
              {Icon && <Icon size={46} color="#fff" strokeWidth={1.8} />}
            </motion.div>
            <motion.p
              data-testid="celebration-message"
              className="text-xl sm:text-2xl font-bold text-white text-center"
              initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.15, duration: 0.3 }}
            >
              {active.title}
            </motion.p>
            {active.detail && (
              <motion.p
                className="text-sm text-white/70 text-center -mt-2 max-w-sm"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25, duration: 0.3 }}
              >
                {active.detail}
              </motion.p>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
